import {ActivitiesState, Activity} from '@target-web/leadads-form-view';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {fetchActivitiesDates, fetchActivitiesDatesRange, fetchActivities} from 'utils/requests';
import {useSearchParams} from 'utils/searchParamsContext';

type useActivitiesProps = {
    active?: boolean;
};

export const useActivities = ({active}: useActivitiesProps): ActivitiesState => {
    const [activities, setActivities] = useState<Activity[]>([]);
    const [maxDate, setMaxDate] = useState('');
    const [selectedDay, setSelectedDay] = useState<string | undefined>(undefined);
    const [availableDates, setAvailableDates] = useState<string[] | false>(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingAvailableDates, setILoadingAvailableDates] = useState(true);
    const {formDataHash} = useSearchParams();

    const searchParams = useMemo(() => {
        const params = new URLSearchParams({lead_form_data: formDataHash, count: '200'});

        if (selectedDay) {
            params.append('from', selectedDay);
            params.append('till', selectedDay);
        }

        return params.toString();
    }, [formDataHash, selectedDay]);

    const getActivities = useCallback(async () => {
        if (!selectedDay) return;
        setIsLoading(true);
        const activities = await fetchActivities(searchParams);
        setActivities(activities);
        setIsLoading(false);
    }, [searchParams, selectedDay]);

    const getDatesRange = useCallback(async () => {
        setMaxDate('');
        setAvailableDates(false);
        setILoadingAvailableDates(true);
        const {min_date, max_date} = await fetchActivitiesDatesRange(`lead_form_data=${formDataHash}`);

        if (min_date && max_date) {
            setMaxDate(max_date);
            const availableDatesData = await fetchActivitiesDates(
                `from=${min_date}&till=${max_date}&lead_form_data=${formDataHash}`,
            );
            availableDatesData.sort((a, b) => (new Date(a) > new Date(b) ? 1 : -1));
            setAvailableDates(availableDatesData);
            if (!selectedDay) {
                setSelectedDay(availableDatesData[0]);
            }
        } else {
            setAvailableDates(false);
        }

        setILoadingAvailableDates(false);
    }, [formDataHash, selectedDay]);

    useEffect(() => {
        if (active) {
            getActivities();
        }
    }, [getActivities, active]);

    useEffect(() => {
        if (active && !selectedDay) {
            getDatesRange();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getDatesRange, active]);

    const setSelectedDayWithLogic = useCallback((d: string) => {
        setSelectedDay((curD) => {
            if (curD !== d) {
                setIsLoading(true);
            }
            return d;
        });
    }, []);

    return {
        isLoadingAvailableDates,
        availableDates,
        isLoading,
        activities,
        maxDate,
        selectedDay,
        setSelectedDay: setSelectedDayWithLogic,
    };
};
